import Hls from "hls.js";
import { Router, VideoPlayer } from "@lightningjs/sdk";
import { Metrics } from "@firebolt-js/sdk";
import theme from "../theme";
import { VideoConfig, videoPlayerDefaults, SubtitleTrackInfo, AudioTrackInfo } from "./interface";
import { getLanguageName, isPostTvPlatform } from "../utils";

// Dynamically load dashjs script if not already available
let dashjsLoading: Promise<any> | null = null;

const loadDashjsScript = (): Promise<any> => {
    console.log("dashjsLoading", dashjsLoading);
    if (dashjsLoading) {
        return dashjsLoading;
    }

    dashjsLoading = new Promise((resolve, reject) => {
        // Check if already loaded
        if (typeof window !== "undefined") {
            const dashjs = (window as any).dashjs || (window as any).dash || (window as any).Dash;
            if (dashjs) {
                resolve(dashjs);
                return;
            }
        }

        // Dynamically load the script
        const script = document.createElement("script");
        script.src = "https://cdn.dashjs.org/v5.0.0/legacy/umd/dash.all.min.js";
        script.async = true;

        script.onload = () => {
            // Wait a bit for dashjs to initialize on window
            const checkDashjs = () => {
                const dashjs = (window as any).dashjs || (window as any).dash || (window as any).Dash;
                if (dashjs) {
                    resolve(dashjs);
                } else {
                    // Retry after a short delay
                    setTimeout(checkDashjs, 50);
                }
            };
            checkDashjs();
        };

        script.onerror = () => {
            console.error("Failed to load dashjs script from CDN");
            reject(new Error("Failed to load dashjs"));
        };

        document.head.appendChild(script);
    });

    return dashjsLoading;
};

const getDashjs = async (): Promise<any> => {
    try {
        return await loadDashjsScript();
    } catch (e) {
        console.error("dashjs library not available");
        return null;
    }
};

let hls: Hls | null = null;
// @ts-ignore - dashjs types
let dashPlayer: any = null;

const handleUnrecoverableError = (player: Hls, errorEvent: string) => {
    player?.destroy();
};

const isHlsSource = (url: string): boolean => {
    return url.indexOf(".m3u") > -1 || url.indexOf(".m3u8") > -1;
};

const isDashSource = (url: string): boolean => {
    return url.indexOf(".mpd") > -1;
};

const unload = (videoEl: HTMLVideoElement) => {
    if (hls?.destroy) {
        hls.destroy();
        hls = null;
    }

    if (dashPlayer) {
        dashPlayer.reset();
        dashPlayer.destroy();
        dashPlayer = null;
    }

    console.log("videoEl", videoEl);
    if (videoEl) {
        videoEl.removeAttribute("src");
        Array.from(videoEl.querySelectorAll("track")).forEach((track) => track.remove());
    }
};

export const loader = (src: string, videoEl: HTMLVideoElement, config: VideoConfig = {}): Promise<void> => {
    return new Promise(async (resolve) => {
        unload(videoEl);

        videoEl.setAttribute("poster", "/static/images/poster.png");
        videoEl.setAttribute("crossorigin", "crossorigin");
        videoEl.style.setProperty("background", theme.colors.bg.primary);

        // Handle HLS sources
        if (
            // !isPostTvPlatform() &&
            isHlsSource(src) &&
            Hls.isSupported()
        ) {
            console.log("Loading hls source:", src);
            hls = new Hls({ ...videoPlayerDefaults, ...config });

            // HLS.js uses EME (Encrypted Media Extensions) for DRM
            // DRM is handled by the browser through EME API
            if (config.drm && config.laUrl) {
                // Enable EME for HLS.js
                // @ts-ignore - HLS.js EME configuration
                hls.config.emeEnabled = true;

                // Note: HLS.js DRM configuration is typically handled through
                // the browser's EME API. The license server URL should be
                // specified in the manifest or through EME key system configuration.
                // For more advanced DRM setup, you may need to configure
                // the key system access directly through EME.
            }

            if (config.subtitles?.length) {
                config.subtitles.forEach((subtitle) => {
                    if (subtitle.url) {
                        const track = document.createElement("track");
                        track.kind = "subtitles";
                        track.label = subtitle.label;
                        track.srclang = subtitle.lang;
                        track.src = subtitle.url;
                        track.default = false;
                        videoEl.appendChild(track);
                    }
                });
            }

            // Helper function to discover and report tracks from manifest
            const discoverTracks = () => {
                const manifestSubtitles: SubtitleTrackInfo[] = [];
                const manifestAudio: AudioTrackInfo[] = [];

                // Access subtitle tracks from HLS instance (parsed from manifest)
                if (hls?.subtitleTracks?.length) {
                    hls.subtitleTracks.forEach((subtitle: any, index: number) => {
                        manifestSubtitles.push({
                            lang: subtitle.lang || subtitle.language || "unknown",
                            url: subtitle.url,
                            kind: "subtitle",
                            id: `manifest-subtitle-${index}`,
                            enabled: subtitle.default || false,
                            label:
                                subtitle.name ||
                                getLanguageName(subtitle.lang || subtitle.language) ||
                                subtitle.lang ||
                                "Unknown"
                        });
                    });
                }

                // Access audio tracks from HLS instance (parsed from manifest)
                if (hls && hls.audioTracks && hls.audioTracks.length > 0) {
                    hls.audioTracks.forEach((audioTrack: any, index: number) => {
                        manifestAudio.push({
                            language: audioTrack.lang || audioTrack.language || "unknown",
                            label:
                                audioTrack.name ||
                                getLanguageName(audioTrack.lang || audioTrack.language) ||
                                audioTrack.lang ||
                                "Unknown",
                            id: `manifest-audio-${index}`
                        });
                    });
                }

                // Call callback if tracks were discovered
                if (config.onTracksDiscovered && (manifestSubtitles.length > 0 || manifestAudio.length > 0)) {
                    config.onTracksDiscovered({
                        subtitles: manifestSubtitles,
                        audio: manifestAudio
                    });
                }
            };

            hls.on(Hls.Events.MEDIA_ATTACHED, function () {
                console.log("video and hls.js are now bound together !");
            });

            // Listen for subtitle tracks update
            hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, function () {
                discoverTracks();
            });

            hls.on(Hls.Events.MANIFEST_PARSED, function (event, data) {
                console.log("manifest loaded, found " + data.levels.length + " quality level");

                // Try to discover tracks after manifest is parsed
                // Use setTimeout to allow HLS.js to populate subtitle tracks
                setTimeout(() => {
                    discoverTracks();
                }, 100);

                resolve();
            });

            hls.on(Hls.Events.ERROR, function (event, data) {
                console.log("ERROR", data);
                if (hls) {
                    if (data.fatal) {
                        switch (data.type) {
                            case Hls.ErrorTypes.NETWORK_ERROR:
                                console.log("fatal network error encountered, try to recover");
                                Metrics.error(Metrics.ErrorType.MEDIA, "NETWORK_ERROR", "playback network error", true);

                                if (navigator.onLine) {
                                    hls.startLoad();
                                    switch (data.details) {
                                        case Hls.ErrorDetails.FRAG_LOAD_ERROR:
                                            if (data.frag) {
                                                hls.currentLevel = data.frag.start + data.frag.duration + 0.1;
                                            } else {
                                                hls.startLoad();
                                            }
                                            break;

                                        case Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
                                            handleUnrecoverableError(hls, event);
                                            break;

                                        default:
                                            hls.startLoad();
                                            break;
                                    }
                                } else {
                                    Router.navigate("!");
                                }

                                break;
                            case Hls.ErrorTypes.MEDIA_ERROR:
                                console.log("fatal media error encountered, try to recover");
                                Metrics.error(Metrics.ErrorType.MEDIA, "MEDIA_ERROR", "playback media error", true);

                                switch (data.details) {
                                    case Hls.ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR:
                                        handleUnrecoverableError(hls, event);
                                        break;
                                    default:
                                        hls.recoverMediaError();
                                        break;
                                }

                                break;
                            default:
                                Metrics.error(
                                    Metrics.ErrorType.MEDIA,
                                    "MEDIA_FATAL_ERROR",
                                    "playback fatal error",
                                    true
                                );

                                // cannot recover
                                handleUnrecoverableError(hls, event);
                                break;
                        }
                    }
                }
            });

            hls.loadSource(src);
            hls.attachMedia(videoEl);

            videoEl.textTracks.onaddtrack = () => {
                toggleSubtitle(config.preferredSubLang || "off");
            };
        }
        // Handle DASH sources
        else if (
            // !isPostTvPlatform() &&
            isDashSource(src)
        ) {
            console.log("Loading dash source:", src);
            getDashjs()
                .then((dashjs) => {
                    if (!dashjs) {
                        console.error("dashjs library not available");
                        resolve();
                        return;
                    }
                    dashPlayer = dashjs.MediaPlayer().create();

                    // Configure DRM for DASH if provided
                    if (config.drm && config.laUrl) {
                        const protectionData: any = {};
                        if (config.drm === "widevine") {
                            protectionData["com.widevine.alpha"] = {
                                serverURL: config.laUrl
                            };
                        } else if (config.drm === "playready") {
                            protectionData["com.microsoft.playready"] = {
                                serverURL: config.laUrl
                            };
                        }
                        console.log("set drm", protectionData);
                        dashPlayer.setProtectionData(protectionData);
                    }

                    // Configure player settings
                    dashPlayer.updateSettings({
                        streaming: {
                            text: {
                                defaultEnabled: true
                            }
                            // buffer: {
                            //     bufferTimeAtTopQuality: videoPlayerDefaults.maxBufferLength,
                            //     bufferTimeAtTopQualityLongForm: videoPlayerDefaults.maxBufferLength
                            // }
                        }
                    });

                    // Handle subtitles for DASH
                    if (config.subtitles?.length) {
                        config.subtitles.forEach((subtitle) => {
                            if (subtitle.url) {
                                const track = document.createElement("track");
                                track.kind = "subtitles";
                                track.label = subtitle.label;
                                track.srclang = subtitle.lang;
                                track.src = subtitle.url;
                                track.default = false;
                                videoEl.appendChild(track);
                            }
                        });
                    }

                    // Event handlers for DASH
                    // Helper function to discover tracks
                    const discoverDashTracks = (eventData?: any) => {
                        const manifestSubtitles: SubtitleTrackInfo[] = [];
                        const manifestAudio: AudioTrackInfo[] = [];

                        try {
                            // Try multiple methods to get tracks
                            let textTracks: any[] = [];
                            let audioTracks: any[] = [];

                            textTracks = dashPlayer.getTracksFor("text") || [];
                            audioTracks = dashPlayer.getTracksFor("audio") || [];

                            console.log("DASH textTracks found:", textTracks.length);
                            console.log("DASH audioTracks found:", audioTracks.length);

                            // Process subtitle tracks - deduplicate by language
                            const seenSubtitleLangs = new Set<string>();
                            textTracks.forEach((track: any, index: number) => {
                                const lang = track.lang || track.language || "unknown";
                                // Only add unique languages (avoid duplicates from multiple codecs)
                                if (!seenSubtitleLangs.has(lang)) {
                                    seenSubtitleLangs.add(lang);
                                    manifestSubtitles.push({
                                        lang: lang,
                                        url: track.url || track.baseURL,
                                        kind: "subtitle",
                                        id: track.id || `manifest-dash-subtitle-${manifestSubtitles.length}`,
                                        enabled: false,
                                        label: track.label || track.role?.value || getLanguageName(lang) || lang
                                    });
                                }
                            });

                            // Process audio tracks
                            audioTracks.forEach((track: any, index: number) => {
                                const lang = track.lang || track.language || "unknown";
                                manifestAudio.push({
                                    language: lang,
                                    label: track.label || track.role?.value || getLanguageName(lang) || lang,
                                    id: track.id || `manifest-dash-audio-${index}`
                                });
                            });
                        } catch (error) {
                            console.warn("Error parsing DASH tracks:", error);
                        }

                        // Call callback if tracks were discovered
                        if (config.onTracksDiscovered && (manifestSubtitles.length > 0 || manifestAudio.length > 0)) {
                            console.log(
                                "Calling onTracksDiscovered with",
                                manifestSubtitles.length,
                                "subtitles and",
                                manifestAudio.length,
                                "audio tracks"
                            );
                            config.onTracksDiscovered({
                                subtitles: manifestSubtitles,
                                audio: manifestAudio
                            });
                        }
                    };

                    dashPlayer.on(dashjs.MediaPlayer.events.STREAM_INITIALIZED, function () {
                        console.log("DASH stream initialized");

                        if (config.startPosition) {
                            dashPlayer.seek(config.startPosition);
                        }
                        // Try to discover tracks after stream is initialized
                        setTimeout(() => {
                            discoverDashTracks();
                        }, 200);
                    });

                    dashPlayer.on(dashjs.MediaPlayer.events.MANIFEST_LOADED, function (event: any) {
                        console.log("DASH manifest loaded", event);
                        // Try to discover tracks after manifest is loaded, pass event data
                        setTimeout(() => {
                            discoverDashTracks(event);
                        }, 200);

                        resolve();
                    });

                    dashPlayer.on(dashjs.MediaPlayer.events.ERROR, function (error: any) {
                        console.log("DASH ERROR", error);
                        Metrics.error(Metrics.ErrorType.MEDIA, "DASH_ERROR", "playback dash error", true);

                        if (
                            error.error &&
                            error.error.code === dashjs.MediaPlayer.errors.MANIFEST_LOADER_PARSING_FAILURE_ERROR_CODE
                        ) {
                            console.log("Fatal DASH error, cannot recover");
                            if (dashPlayer) {
                                dashPlayer.reset();
                                dashPlayer.destroy();
                                dashPlayer = null;
                            }
                        } else if (navigator.onLine) {
                            // Try to recover
                            console.log("Attempting to recover from DASH error");
                        } else {
                            Router.navigate("!");
                        }
                    });

                    console.log("set src", src);

                    dashPlayer.initialize(videoEl, src, config.autoplay !== false);

                    if (config.startPosition) {
                        dashPlayer.seek(config.startPosition);
                    }

                    videoEl.textTracks.onaddtrack = () => {
                        toggleSubtitle(config.preferredSubLang || "off");
                    };
                })
                .catch((error) => {
                    console.error("Failed to load dashjs:", error);
                    resolve();
                });
        }
        // Fallback to native video element
        else {
            videoEl.setAttribute("src", src);
            videoEl.load();

            videoEl.currentTime = config.startPosition || 0;

            videoEl
                .play()
                .then((r) => console.log("play"))
                .catch((e) => console.log(e));

            resolve();
        }
    });
};

export const unloader = (videoEl: HTMLVideoElement): Promise<void> => {
    return new Promise((resolve) => {
        unload(videoEl);
        resolve();
    });
};

export const setPlaybackSpeed = (speed: number) => {
    if (hls) {
        const videoEl = hls.media;
        if (videoEl) {
            videoEl.playbackRate = speed;
            console.log(`HLS playback speed set to ${speed}`);
        } else {
            console.warn("HLS media element not found for playback speed adjustment.");
        }
    } else if (dashPlayer) {
        const videoEl = dashPlayer.getVideoElement();
        if (videoEl) {
            videoEl.playbackRate = speed;
            console.log(`DASH playback speed set to ${speed}`);
        } else {
            console.warn("DASH media element not found for playback speed adjustment.");
        }
    } else {
        const videoEl = VideoPlayer._videoEl;
        if (videoEl) {
            videoEl.playbackRate = speed;
            console.log(`Default video playback speed set to ${speed}`);
        } else {
            console.warn("Default video element not found for playback speed adjustment.");
        }
    }
};

export const toggleSubtitle = (lang: string) => {
    // Handle DASH subtitle tracks first
    if (dashPlayer) {
        try {
            const textTracks = dashPlayer.getTracksFor("text") || [];

            if (lang === "off") {
                // Disable subtitles by setting current track to null or -1
                dashPlayer.setCurrentTrack(null);
            } else {
                const index = textTracks.findIndex((t: any) => (t.lang || t.language) === lang);

                if (index) {
                    dashPlayer.setTextTrack(index);
                    dashPlayer.enableText(true);
                    console.log(`DASH subtitle track set to: ${lang}, ${index}`);
                } else {
                    console.warn(`DASH subtitle track not found for language: ${lang}`);
                }
                // return;
            }
        } catch (error) {
            console.warn("Error setting DASH subtitle track:", error);
        }
    }

    // Handle HLS and fallback to video element text tracks
    let videoEl;
    if (hls) {
        videoEl = hls.media;
    } else if (dashPlayer) {
        videoEl = dashPlayer.getVideoElement();
    } else {
        videoEl = VideoPlayer._videoEl;
    }

    if (videoEl) {
        const tracks = Array.from(videoEl.textTracks);
        if (lang === "off") {
            tracks.forEach((track: any) => (track.mode = "disabled"));
        } else {
            tracks.forEach((track: any) => {
                track.mode = track.language === lang ? "showing" : "disabled";
            });
        }
    } else {
        console.warn("Video element not found for subtitle adjustment.");
    }
};

export const toggleAudio = (lang: string) => {
    // Handle HLS audio tracks
    if (hls && hls.audioTracks && hls.audioTracks.length > 0) {
        const audioTracks = hls.audioTracks;
        const trackIndex = audioTracks.findIndex((track: any) => track.lang === lang || track.language === lang);
        if (trackIndex !== -1) {
            hls.audioTrack = trackIndex;
            console.log(`HLS audio track set to: ${lang} (index: ${trackIndex})`);
        } else {
            console.warn(`HLS audio track not found for language: ${lang}`);
        }
        return;
    }

    // Handle DASH audio tracks
    if (dashPlayer) {
        try {
            // Use getTracksFor to get audio tracks
            const audioTracks = dashPlayer.getTracksFor("audio") || [];
            const track = audioTracks.find((t: any) => (t.lang || t.language) === lang);

            if (track) {
                // Use setCurrentTrack to switch audio track
                dashPlayer.setCurrentTrack(track);
                console.log(`DASH audio track set to: ${lang}`);
            } else {
                console.warn(`DASH audio track not found for language: ${lang}`);
            }
        } catch (error) {
            console.warn("Error setting DASH audio track:", error);
        }
        return;
    }

    // Fallback: try to use video element audio tracks (if available)
    const videoEl = VideoPlayer._videoEl;
    if (videoEl) {
        const videoElWithAudioTracks = videoEl as HTMLVideoElement & { audioTracks?: any };
        if (videoElWithAudioTracks.audioTracks && videoElWithAudioTracks.audioTracks.length > 0) {
            try {
                Array.from(videoElWithAudioTracks.audioTracks).forEach((audioTrack: any, index: number) => {
                    if (audioTrack.language === lang || audioTrack.lang === lang) {
                        audioTrack.enabled = true;
                    } else {
                        audioTrack.enabled = false;
                    }
                });
                console.log(`Audio track set to: ${lang}`);
            } catch (e) {
                console.warn("Could not set audio track:", e);
            }
        }
    } else {
        console.warn("Video element not found for audio track adjustment.");
    }
};

export const getPlaybackSpeed = () => {
    if (hls) {
        return hls.media?.playbackRate || 1;
    } else if (dashPlayer) {
        const videoEl = dashPlayer.getVideoElement();
        return videoEl?.playbackRate || 1;
    }
    return 1;
};

export const destroyPlayer = () => {
    if (hls) {
        hls.destroy();
        hls = null;
    }
    if (dashPlayer) {
        dashPlayer.reset();
        dashPlayer.destroy();
        dashPlayer = null;
    }
};

export const handleRewind = (_rewindTime: number, callback: (isBitmovin: boolean) => void) => {
    callback(false);
};

export const handleForward = (forwardTime: number, callback: (isBitmovin: boolean) => void) => {
    callback(false);
};
